import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Flower2,
  Sparkles,
  Baby,
  Flame,
  Brain,
  Heart,
  Stethoscope,
  Hourglass,
} from 'lucide-react';
import { healthApi } from '@/api/healthApi';
import WomensHealthPreview from '@/components/WomensHealthPreview';

/**
 * Women's Health Hub — life-stage-aware entry point.
 *
 * Mirrors the same three-mode pattern as <MensHealthHub />:
 *   - 'live'        drills into existing forms (menstrual / hormonal / pregnancy / screening)
 *   - 'preview'     opens an inline preview screen built from today's tracker data
 *   - 'coming-soon' rendered as a non-clickable, slightly dimmed card
 *
 * Soft pink/purple palette tuned to feel emotionally intelligent without
 * being overly clinical. NO popups or blocking modals anywhere in this flow.
 */
const SECTION_DEFINITIONS = [
  {
    key: 'menstrual',
    icon: Flower2,
    title: 'Menstrual & Cycle Health',
    description: 'Track your cycle, symptoms, and reproductive patterns.',
    cta: 'Track Cycles',
    accent: 'border-l-pink-400 bg-pink-50',
    iconWrap: 'bg-pink-100 text-pink-700',
    section: 'menstrual',
    mode: 'live',
  },
  {
    key: 'hormonal',
    icon: Sparkles,
    title: 'Hormonal Health',
    description: 'Understand hormone balance, mood changes, and menopause support.',
    cta: 'Check Hormonal Health',
    accent: 'border-l-fuchsia-400 bg-fuchsia-50',
    iconWrap: 'bg-fuchsia-100 text-fuchsia-700',
    route: '/hormonal-health?context=women',
    mode: 'live',
  },
  {
    key: 'pregnancy',
    icon: Baby,
    title: 'Pregnancy & Fertility',
    description: 'Plan for pregnancy, fertility, and prenatal wellbeing.',
    cta: 'Start Planning',
    accent: 'border-l-rose-400 bg-rose-50',
    iconWrap: 'bg-rose-100 text-rose-700',
    section: 'pregnancy',
    mode: 'live',
  },
  {
    key: 'sexual',
    icon: Flame,
    title: 'Sexual & Intimate Health',
    description: 'Support libido, vaginal health, and reproductive wellbeing.',
    cta: 'Open preview',
    accent: 'border-l-orange-400 bg-orange-50',
    iconWrap: 'bg-orange-100 text-orange-700',
    mode: 'preview',
    pillLabel: 'Preview',
  },
  {
    key: 'mental',
    icon: Brain,
    title: 'Mental & Emotional Health',
    description: 'Manage stress, mood changes, and emotional wellbeing.',
    cta: 'Check Mental Health',
    accent: 'border-l-purple-400 bg-purple-50',
    iconWrap: 'bg-purple-100 text-purple-700',
    route: '/mental-health?context=women',
    mode: 'live',
  },
  {
    key: 'heart',
    icon: Heart,
    title: 'Heart & General Health',
    description: 'Monitor heart health, metabolism, and overall wellbeing.',
    cta: 'Open preview',
    accent: 'border-l-red-300 bg-red-50',
    iconWrap: 'bg-red-100 text-red-700',
    mode: 'preview',
    pillLabel: 'Preview',
  },
  {
    key: 'screening',
    icon: Stethoscope,
    title: 'Preventive Screenings',
    description: 'Stay on top of mammograms, cervical screening, and bone health.',
    cta: 'Check Screening Needs',
    accent: 'border-l-teal-400 bg-teal-50',
    iconWrap: 'bg-teal-100 text-teal-700',
    section: 'screening',
    mode: 'live',
  },
  {
    key: 'life-stages',
    icon: Hourglass,
    title: 'Life Stage Guidance',
    description: 'Get guidance for every stage — from early adulthood to menopause.',
    cta: 'Open preview',
    accent: 'border-l-indigo-300 bg-indigo-50',
    iconWrap: 'bg-indigo-100 text-indigo-700',
    mode: 'preview',
    pillLabel: 'Preview',
  },
];

const microcopyForScore = (score) => {
  if (score == null) return null;
  if (score >= 70) return "You're doing well — keep supporting your balance.";
  if (score >= 50) return 'A few areas need attention today.';
  return "Let's gently improve your health step by step.";
};

const WomensHealthHub = ({ onSelectSection }) => {
  const navigate = useNavigate();
  const [score, setScore] = useState(null);
  const [tracker, setTracker] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    healthApi
      .getDailyTracker()
      .then((res) => {
        if (cancelled) return;
        const t = res?.data?.tracker;
        if (t) {
          setTracker(t);
          if (typeof t.health_score === 'number') setScore(t.health_score);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const microcopy = useMemo(() => microcopyForScore(score), [score]);

  const handleCardClick = (def) => {
    if (def.mode === 'live') {
      if (def.route) {
        navigate(def.route);
        return;
      }
      onSelectSection(def.section);
      return;
    }
    if (def.mode === 'preview') {
      setPreviewKey(def.key);
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  if (previewKey) {
    return (
      <WomensHealthPreview
        previewKey={previewKey}
        tracker={tracker}
        onBack={() => setPreviewKey(null)}
      />
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-pink-50/40 via-white to-white pb-24"
      data-testid="womens-health-hub"
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-5"
          data-testid="womens-health-hub-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <header className="mb-7">
          <h1
            className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight"
            data-testid="womens-health-hub-title"
          >
            Women's Health
          </h1>
          <p
            className="text-sm sm:text-base text-gray-600 mt-2 leading-relaxed"
            data-testid="womens-health-hub-subtitle"
          >
            Support your body through every stage of life.
          </p>
          {microcopy && (
            <p
              className="text-sm text-pink-700 font-medium mt-3 inline-flex items-center gap-1.5 bg-pink-50 border border-pink-100 rounded-full px-3 py-1"
              data-testid="womens-health-hub-microcopy"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-pink-500" />
              {microcopy}
            </p>
          )}
        </header>

        <div className="space-y-3" data-testid="womens-health-hub-list">
          {SECTION_DEFINITIONS.map((def) => {
            const Icon = def.icon;
            const isComingSoon = def.mode === 'coming-soon';
            const isPreview = def.mode === 'preview';
            const Wrapper = isComingSoon ? 'div' : 'button';
            const wrapperProps = isComingSoon
              ? { 'aria-disabled': true }
              : {
                  onClick: () => handleCardClick(def),
                  type: 'button',
                };
            const dimClass = isComingSoon
              ? 'opacity-90 cursor-default'
              : isPreview
              ? 'opacity-95 hover:opacity-100 hover:shadow-md active:scale-[0.99]'
              : 'hover:shadow-md active:scale-[0.99]';
            const ctaTextClass = isComingSoon
              ? 'text-gray-500'
              : 'text-gray-900 group-hover:gap-1.5';
            const pillClass = isComingSoon
              ? 'bg-gray-100 text-gray-500 border-gray-200'
              : isPreview
              ? 'bg-white text-pink-700 border-pink-200'
              : 'bg-white/70 text-gray-600 border-gray-200';

            return (
              <Wrapper
                key={def.key}
                {...wrapperProps}
                className={`group w-full text-left rounded-2xl border border-gray-100 ${def.accent} border-l-4 px-4 sm:px-5 py-4 shadow-sm transition-all ${dimClass}`}
                data-testid={`womens-health-card-${def.key}`}
                data-mode={def.mode}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${def.iconWrap}`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-base font-bold text-gray-900 leading-tight">
                        {def.title}
                      </p>
                      {def.pillLabel && (
                        <span
                          className={`text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-md border ${pillClass}`}
                          data-testid={`womens-health-card-${def.key}-pill`}
                        >
                          {def.pillLabel}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 leading-snug mt-1">
                      {def.description}
                    </p>
                    <p
                      className={`text-sm font-semibold mt-2.5 inline-flex items-center gap-1 transition-all ${ctaTextClass}`}
                    >
                      {def.cta}
                      {!isComingSoon && <ArrowRight className="h-3.5 w-3.5" />}
                    </p>
                  </div>
                </div>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WomensHealthHub;
